import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { ArrowUpRight } from "@phosphor-icons/react";

import { BLOCK_BORDER_COLOR } from "../constants";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GoalResource {
  /** Unique identifier for the resource */
  id: string;
  /** Display label shown on the chip */
  label: string;
  /** URL of the resource */
  url: string;
}

export interface ResourcesCardProps {
  /** List of resource links to display */
  resources: GoalResource[];
  /** Additional MUI sx overrides for the card container */
  sx?: SxProps<Theme>;
}

// ---------------------------------------------------------------------------
// ResourcesCard
// ---------------------------------------------------------------------------

/**
 * Sidebar card listing resource links attached to a goal.
 * Each resource renders as a button with an external-link icon on the right.
 *
 * @param props
 * @param props.resources        - List of resource items to render
 * @param [props.sx]             - Additional MUI sx overrides
 */
const ResourcesCard: React.FC<ResourcesCardProps> = ({ resources, sx }) => {
  return (
    <Box
      component="section"
      aria-label="Goal resources"
      sx={[
        {
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          bgcolor: "background.paper",
          border: `1px solid ${BLOCK_BORDER_COLOR}`,
          borderRadius: "12px",
          px: 5,
          py: 5,
          width: "100%",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {/* Heading */}
      <Typography component="h2" variant="body2" color="text.primary">
        Resources
      </Typography>

      {/* Resource links */}
      {resources.length > 0 && (
        <Stack
          component="ul"
          aria-label="Resource links"
          direction="row"
          gap={3}
          flexWrap="wrap"
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
          }}
        >
          {resources.map((resource) =>
            resource.url ? (
              <Box component="li" key={resource.id}>
                <Button
                  component="a"
                  href={resource.url ?? ""}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  color="inherit"
                  size="small"
                  startIcon={<ArrowUpRight size={16} />}
                  aria-label={`Open ${resource.label} in new tab`}
                  title={resource.label}
                  sx={{
                    borderColor: "divider",
                    borderRadius: "10px",
                    height: "auto",
                    boxShadow: "0px 1px 1px 0px rgba(0,0,0,0.15)",
                    py: 1,
                  }}
                >
                  {/* Truncate the label text if it exceeds the max width */}
                  <Box component="span" sx={{
                    display: "inline-block",
                    maxWidth: "250px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                    {resource.label}
                  </Box>
                </Button>
              </Box>
            ) : null
          )}
        </Stack>
      )}
    </Box>
  );
};

export default ResourcesCard;
